// src/lib/components/molecules/AtlasTree/AtlasTree.tsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Node from '../Node/Node'; // Node component
import './AtlasTree.scss';
import { Toast } from 'primereact/toast';

import { Skeleton } from 'primereact/skeleton';
import AtlasHeader from '../../../atoms/atlas-header/atlas-header';
import 'primeflex/primeflex.css';
import { PermissionList } from '../../../../../types/sharedTypes';
import AddSphere from '../../AddSphere/AddSphere'; // Assuming AddSphere already exists
import DialogWrapper from '../../DialogWrapper/DialogWrapper'; // Use your dialog wrapper component
import { Sphere } from 'types/global';
import type { RuntimeFilterEnvelope, SphereNode } from 'types/context';
import type { FilterExpression, PerspectiveFilter, UnifiedFilters } from 'types/filters';
import type { FilterSortState } from '../../../atoms/atlas-header/atlas-header';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { concatUnifiedFilters } from '@center-platform/spheres/filters';
import { useContextTree } from '../../../../hooks/useContextTree';
import { sphereNodeToSphere } from '../../../../adapters/sphereNodeToSphere';
import debounce from 'lodash.debounce';
import { AtlasViewContext } from '../../../organisms/AtlasComponent/AtlasViewContext';
import { deriveEnrichmentFromMolecules, deriveColumnPropertiesFromMolecules } from './enrichmentUtils';
import EditSphere from '../EditSphere/EditSphere';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useTranslation } from 'localization';
import AddTemplate from '../../AddTemplate/AddTemplate'; // Assuming AddTemplate already exists
import { StatusPopoverProvider } from '../StatusPopover/StatusPopoverContext';
import { useSpherePolling } from '../../../../hooks/useSpherePolling';
import { useMetaspaceEvents } from '../../../../hooks/useMetaspaceEvents';
import { useToolbarContextOrNull } from '../../../../context/ToolbarContext';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { useUseCases } from '../../../../hooks/useUseCases';
import InlineAddSphereRowMolecule from '../../InlineAddSphereRow/InlineAddSphereRowMolecule';
import { useImpersonation, getTokenClaims } from '@center-technologies/shared-api-clients';
import { register } from '../../../../hooks/spherePermissionCache';
import type { SpherePermissionAction } from '../../../../hooks/spherePermissionCache';
import AddSphereDialogOrganism from '../../../organisms/AddSphereDialog/AddSphereDialogOrganism';
import AtlasEmptyStateMolecule from '../AtlasEmptyState/AtlasEmptyStateMolecule';
import { useAvailableTypes } from '../../../../hooks/useAvailableTypes';
import { useEligibleApproaches } from '../../../../hooks/useEligibleApproaches';
import { getRecentTypes } from '../AtlasEmptyState/atlasRecentTypes';

// Define TabItem type if not imported from elsewhere
type TabItem = {
  id: string;
  systemName: string;
  name: string;
  description?: string;
  avatar?: string;
  tabType: string;
  children?: TabItem[];
};

interface AtlasTreeProps {
  sphereId: string;
  pageLimit: number | null;
  onChatBubbleClick?: (sphereId: string) => void;
  containerWidth?: number;
}

const AtlasTree: React.FC<AtlasTreeProps> = React.memo(({ sphereId, pageLimit, onChatBubbleClick, containerWidth = 0 }) => {
  const apiClients = useApiClientsOrNull();
  const { t } = useTranslation();
  const { emitSphereCreated, emitSphereUpdated, emitSphereStatusChanged, emitSphereCentered, emitToolbarExpand, lastEvent } = useMetaspaceEvents();

  // Non-null only when Atlas is rendered inside a metaspace with the toolbar
  // mounted (MetaspaceGrid wraps the grid in <ToolbarProvider> iff showToolbar).
  // That presence IS the "inside a metaspace toolbar" signal — no separate flag.
  const toolbarContext = useToolbarContextOrNull();

  // When the toolbar is present, a row's status-icon click opens the toolbar's
  // status tool scoped to that sphere (carried on the TOOLBAR_EXPAND payload).
  // Undefined otherwise, so standalone Atlas keeps the inline status popover.
  const handleStatusToolClick = useCallback(
    (statusSphereId: string) => {
      emitToolbarExpand('status', statusSphereId);
    },
    [emitToolbarExpand]
  );
  const [expandedKeys, setExpandedKeys] = useState<{
    [key: string]: boolean;
  }>({});
  const [breadcrumbPath, setBreadcrumbPath] = useState<string>(''); // State to track breadcrumb path
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);
  const [nodeSphere, setNodeSphere] = useState<Sphere | null>(null);
  const [nodeParentSphere, setNodeParentSphere] = useState<Sphere | null>(null);
  const [headerWidths, setHeaderWidths] = useState<
    {
      label: any;
      width: number;
      className: string;
      perspectiveSystemName: string;
    }[]
  >([]);
  const observersRef = useRef<{ [key: string]: IntersectionObserver }>({});

  const [nodeGrandparentSphere, setNodeGrandparentSphere] = useState<Sphere | null>(null);
  const treeRef = useRef<HTMLUListElement>(null); // Ref to the tree container
  const [activeNodeSection, setActiveNodeSection] = useState<{
    key: string | null;
    section: string | null;
  }>({ key: null, section: null });
  const [editViewMode, setEditViewMode] = useState<boolean>(false);
  const [initialEditorTab, setInitialEditorTab] = useState<string | undefined>(undefined);
  const [selectedResponsiblePerspective, setSelectedResponsiblePerspective] = useState<any[]>([]);
  const [selectedNodePermissionList, setSelectedNodePermissionList] = useState<PermissionList>({ permissions: {} });
  const [selectedPerspective, setSelectedPerspective] = useState<string | null>(null);
  const [updatedSelectedOptions, setUpdatedSelectedOptions] = useState<any[]>([]); // State for selected options
  const [allNodesVisible, setAllNodesVisible] = useState(false);

  // pagination state
  const [isFetching, setIsFetching] = useState(false);
  const [selectedEdgeCountNode, setSelectedEdgeCountNode] = useState<string | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [featuredSphereId, setFeaturedSphereId] = useState(sphereId);
  const [nodeDirections, setNodeDirections] = useState<{
    [key: string]: 'outwards' | 'inwards' | 'both';
  }>({}); // State to store traversal direction per node
  const toast = useRef<Toast>(null);
  const [nodeWidths, setNodeWidths] = useState<number[]>([]);
  const context = React.useContext(AtlasViewContext);
  const {
    contextSystemName: firstContext,
    viewConfig,
    composedColumnFilters = { runtime: {}, display: {}, provenance: [] },
    columnFilterSaveTargets = [],
    onColumnFilterSave = () => {
      /* no-op outside AtlasComponent's provider */
    },
    activeViewDisplayName,
    arcTemplates = [],
    allSchemas = [],
  } = context ?? {
    contextSystemName: '',
    viewConfig: { type: 'atlas' as const, nodeMolecules: [] },
  };
  const [headerWidth, setHeaderWidth] = useState<number>(0);
  const [templateDialogVisible, setTemplateDialogVisible] = useState(false);

  const enrichments = useMemo(
    () => deriveEnrichmentFromMolecules(viewConfig.nodeMolecules ?? []),
    [viewConfig.nodeMolecules]
  );
  // Custom sphere properties (dates, text columns) the view's molecules render.
  // Threaded to execute so the Gremlin valueMap projects them; otherwise these
  // columns come back empty.
  const columnProperties = useMemo(
    () => deriveColumnPropertiesFromMolecules(viewConfig.nodeMolecules ?? []),
    [viewConfig.nodeMolecules]
  );
  // Side-effect call: lazily warms the use-cases cache for descendant pickers.
  useUseCases();
  // The root sphere's type drives the header's perspective/property columns —
  // atlas-header resolves each PerspectiveSelector against baseSchema.perspectives.
  // Set via effect once the tree data lands (was previously stubbed to undefined,
  // which left baseSchema null and dropped every perspective column header).
  const [baseSchemaType, setBaseSchemaType] = useState<string | null>(null);
  const { schema: baseSchema } = useSchemaType(baseSchemaType);

  const [nodeNotifications, setNodeNotifications] = useState<{
    [nodeId: string]: {
      type: 'success' | 'error';
      message: string;
      timer?: number;
    };
  }>({});


  // Add state to control the AddSphere dialog visibility
  const [addSphereDialogVisible, setAddSphereDialogVisible] = useState(false);
  const [addSphereV2DialogVisible, setAddSphereV2DialogVisible] = useState(false);
  const [addSphereV2InitialTab, setAddSphereV2InitialTab] = useState<'blank' | 'template'>('blank');
  const [addSphereV2InitialMode, setAddSphereV2InitialMode] = useState<'create' | 'deploy'>('create');
  // Pre-selected type when opening the dialog from a "Start blank" chip — opens
  // straight on the Configure step for that type.
  const [addSphereV2InitialSchema, setAddSphereV2InitialSchema] = useState<any>(null);
  const [deployApproachTargetSphere, setDeployApproachTargetSphere] = useState<Sphere | null>(null);
  const [editSphereDialogVisible, setEditSphereDialogVisible] = useState(false);
  const [selectedTemplateSchema, setSelectedTemplateSchema] = useState<any>(null);
  const firstHeaderRef = useRef<HTMLDivElement>(null);
  const [selectedSphereType, setSelectedSphereType] = useState<{
    sphereType: string;
    source: 'type' | 'usecase';
    label: string;
  } | null>(null);

  // Inline add row state (draft sphere quick-capture). The row is docked above the
  // pagination footer (outside the scrolling list) so it's always visible and can't be
  // pushed out of reach by Atlas's infinite-scroll pagination.
  const [inlineAddParentId, setInlineAddParentId] = useState<string | null>(null);

  // Filter/sort state management — drives runtime filters for context execution
  const [filterSortState, setFilterSortState] = useState<FilterSortState>({});

  const addOrUpdatePropertyFilter = useCallback((filter: FilterExpression) => {
    setFilterSortState((prev) => {
      const existing = prev.filters?.propertyFilters ?? [];
      const idx = existing.findIndex((f) => f.property === filter.property);
      const updated = idx >= 0
        ? existing.map((f, i) => (i === idx ? filter : f))
        : [...existing, filter];
      return { ...prev, filters: { ...prev.filters, propertyFilters: updated } };
    });
  }, []);

  const removePropertyFilterByKey = useCallback((property: string) => {
    setFilterSortState((prev) => {
      const existing = prev.filters?.propertyFilters ?? [];
      const updated = existing.filter((f) => f.property !== property);
      const filters = { ...prev.filters, propertyFilters: updated };
      if (!filters.propertyFilters?.length && !filters.perspectiveFilters?.length && !filters.filterGroups?.length) {
        return { ...prev, filters: undefined };
      }
      return { ...prev, filters };
    });
  }, []);

  const addOrUpdatePerspectiveFilter = useCallback((filter: PerspectiveFilter) => {
    setFilterSortState((prev) => {
      const existing = prev.filters?.perspectiveFilters ?? [];
      const idx = existing.findIndex((f) => f.perspectiveSystemName === filter.perspectiveSystemName);
      const updated = idx >= 0
        ? existing.map((f, i) => (i === idx ? filter : f))
        : [...existing, filter];
      return { ...prev, filters: { ...prev.filters, perspectiveFilters: updated } };
    });
  }, []);

  const addOrUpdateSortOrder = useCallback((sort: any) => {
    setFilterSortState((prev) => {
      const existing = prev.sortOrder ?? [];
      const idx = existing.findIndex((s: any) => s.property === sort.property);
      const updated = idx >= 0
        ? existing.map((s: any, i: number) => (i === idx ? sort : s))
        : [...existing, sort];
      return { ...prev, sortOrder: updated };
    });
  }, []);

  const removeSortOrderByKey = useCallback((property: string) => {
    setFilterSortState((prev) => {
      const updated = (prev.sortOrder ?? []).filter((s: any) => s.property !== property);
      return { ...prev, sortOrder: updated.length > 0 ? updated : undefined };
    });
  }, []);

  const resetFilterSortState = useCallback(() => {
    setFilterSortState({});
  }, []);

  // Derive runtime filters from filter state, debounced to avoid excessive re-fetches
  const [debouncedFilters, setDebouncedFilters] = useState<UnifiedFilters | undefined>(undefined);
  const debouncedSetFilters = useMemo(
    () => debounce((filters: UnifiedFilters | undefined) => {
      setDebouncedFilters(filters);
    }, 400),
    []
  );

  useEffect(() => {
    debouncedSetFilters(filterSortState.filters);
    return () => { debouncedSetFilters.cancel(); };
  }, [filterSortState.filters, debouncedSetFilters]);

  // AtlasTree's column filters apply to every level — explicitly wrap them in
  // the `global` lane of the envelope rather than relying on the backend's
  // implicit bare-`UnifiedFilters` classification (EAINT-781 Phase 6), so the
  // wire shape states its own intent instead of depending on a fallback rule
  // living in a different service.
  //
  // Two sources feed `global` (EAINT-931): the legacy `filterSortState`
  // (urgency/sphere-type/perspective — not yet migrated to the level-filter
  // stack) and `composedColumnFilters.runtime` (Aspect columns' context/view/
  // user/session stack via `LevelFilterPopoverOrganism`, scope `'*'`).
  const debouncedEnvelope = useMemo<RuntimeFilterEnvelope | undefined>(() => {
    const merged = concatUnifiedFilters(composedColumnFilters?.runtime, debouncedFilters);
    return merged ? { global: merged } : undefined;
  }, [composedColumnFilters?.runtime, debouncedFilters]);

  // Use context tree hook for data fetching
  const {
    roots: contextRoots,
    loading: _contextLoading,
    error: _contextError,
    refresh: contextRefresh,
    expandNode: contextExpandNode,
    collapseNode: contextCollapseNode,
    hasMore: contextHasMore,
    loadMore: contextLoadMore,
    expandedKeys: _contextExpandedKeys,
    data: _contextData,
  } = useContextTree(apiClients, {
    systemName: firstContext ?? '',
    rootSphereId: featuredSphereId,
    limit: pageLimit ?? 50,
    enrichmentOverrides: enrichments,
    runtimeFilters: debouncedEnvelope,
    columnProperties,
  });

  // Fetch ancestors for the root sphere (needed for breadcrumbs / navigation)
  const [rootAncestors, setRootAncestors] = useState<Sphere[]>([]);
  // Which sphere the ancestors we hold actually describe.
  const [ancestorsSphereId, setAncestorsSphereId] = useState<string | null>(null);
  useEffect(() => {
    if (!featuredSphereId || !apiClients?.graphClient) {
      setRootAncestors([]);
      setAncestorsSphereId(null);
      return;
    }
    let cancelled = false;
    // Drop the previous root's ancestors up front. They used to survive the re-root and
    // get attached to the new root below, producing a breadcrumb that spliced the old
    // chain onto the new leaf until the real ancestors landed.
    setRootAncestors([]);
    setAncestorsSphereId(null);
    apiClients.graphClient
      .get(`/spheres/${featuredSphereId}/ancestors`)
      .then((res: any) => {
        if (!cancelled) {
          setRootAncestors(res?.ancestors ?? []);
          setAncestorsSphereId(featuredSphereId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRootAncestors([]);
          setAncestorsSphereId(featuredSphereId);
        }
      });
    return () => { cancelled = true; };
  }, [featuredSphereId, apiClients?.graphClient]);

  // Convert SphereNode tree to legacy Sphere tree for downstream components
  const nodesData: Sphere[] = useMemo(() => {
    const nodes = (contextRoots ?? []).map(sphereNodeToSphere);
    // Attach ancestors to the root sphere so breadcrumbs work — but only the ancestors
    // that belong to this root.
    if (nodes.length > 0 && rootAncestors.length > 0 && ancestorsSphereId === nodes[0].id) {
      nodes[0] = { ...nodes[0], ancestors: rootAncestors };
    }
    return nodes;
  }, [contextRoots, rootAncestors, ancestorsSphereId]);

  // Drive the header's schema (perspective/property columns) from the root node's type.
  useEffect(() => {
    setBaseSchemaType(nodesData[0]?.properties?.type ?? null);
  }, [nodesData]);

  // Narrow the Stage column's template vocabulary to what the visible board rows
  // actually use (EAINT-936 OQ-11) — `useArcTemplates()` returns the org's entire
  // published catalog, which is ≈never a single template for a real org, so the
  // `onlyTemplate` heuristic (`LevelFilterPopoverOrganism`) never fires against it.
  // Mirrors `AtlasArcStageSelectorMolecule`'s own `sphere.arcs[]` primary-arc read.
  // Falls back to the full catalog when no row has a primary arc yet (empty board,
  // still loading).
  const inUseArcTemplates = useMemo(() => {
    const inUseNames = new Set<string>();
    // `nodesData` is a tree (root(s) + nested `nearbySpheres`), not a flat row
    // list — walk every visible row, the same shape the tree actually renders.
    const collect = (spheres: Sphere[]): void => {
      spheres.forEach((sphere) => {
        const primaryTemplate = sphere.arcs?.find((a) => a.isPrimary)?.templateSystemName;
        if (primaryTemplate) inUseNames.add(primaryTemplate);
        if (sphere.nearbySpheres?.length) collect(sphere.nearbySpheres);
      });
    };
    collect(nodesData);
    if (inUseNames.size === 0) return arcTemplates;
    const narrowed = arcTemplates.filter((tpl) => inUseNames.has(tpl.systemName));
    return narrowed.length > 0 ? narrowed : arcTemplates;
  }, [nodesData, arcTemplates]);

  // Re-provide `AtlasViewContext` with the narrowed catalog so `atlas-header`'s
  // `context?.arcTemplates ?? arcTemplates` (which prefers context over the prop
  // above) picks up the narrowed set too — the outer provider (`AtlasComponent`)
  // still exposes the org-wide catalog to everything outside this subtree.
  const narrowedViewContext = useMemo(
    () => (context ? { ...context, arcTemplates: inUseArcTemplates } : context),
    [context, inUseArcTemplates]
  );

  // Loading state per node (for skeleton display during expand)
  const [loadingNearby, setLoadingNearby] = useState<{ [key: string]: boolean }>({});
  const fetchingRef = useRef<{ [key: string]: boolean }>({});
  // Circuit breaker: a runaway — e.g. an infinite-scroll observer that re-arms
  // without the child list ever growing — can fire child-load requests in a tight
  // loop. We track recent fire timestamps per parent and hard-stop a node that
  // exceeds the threshold, independent of any hasMore/pagination logic. This is
  // belt-and-suspenders on top of useContextTree's no-progress termination, so
  // Atlas can never storm graph-service with children?parentSphereId requests.
  const loadMoreFiresRef = useRef<{ [key: string]: number[] }>({});
  const circuitOpenRef = useRef<{ [key: string]: boolean }>({});

  // Wrap expandNode to also track loading state
  const fetchNearbySpheres = useCallback(
    async (sphereId: string, _token?: string, _direction?: string, _surface?: string) => {
      // Guard against concurrent fetches for the same parent
      if (fetchingRef.current[sphereId]) return;

      // Circuit breaker: hard-stop a parent that fires child-load requests in a
      // tight loop, regardless of cause. Once open it stays open for this node
      // for the life of the mount (a refresh/navigation resets it); this bounds
      // the blast radius of any future re-arm bug to a handful of requests.
      if (circuitOpenRef.current[sphereId]) return;
      const CIRCUIT_WINDOW_MS = 3000;
      const CIRCUIT_MAX_FIRES = 12;
      const now = Date.now();
      const recentFires = (loadMoreFiresRef.current[sphereId] ?? []).filter(
        (t) => now - t < CIRCUIT_WINDOW_MS
      );
      recentFires.push(now);
      loadMoreFiresRef.current[sphereId] = recentFires;
      if (recentFires.length > CIRCUIT_MAX_FIRES) {
        circuitOpenRef.current[sphereId] = true;
        console.error(
          `[AtlasTree] Circuit breaker OPEN for parent ${sphereId}: ` +
            `${recentFires.length} child-load requests within ${CIRCUIT_WINDOW_MS}ms. ` +
            `Halting further loads for this node to prevent a request storm.`
        );
        return;
      }

      fetchingRef.current[sphereId] = true;

      // Determine the level of this sphere in the tree
      const findLevel = (nodes: SphereNode[], id: string): number => {
        for (const node of nodes) {
          if (node.id === id) return node.level;
          if (node.children.length > 0) {
            const found = findLevel(node.children, id);
            if (found >= 0) return found;
          }
        }
        return -1;
      };
      const level = contextRoots ? findLevel(contextRoots, sphereId) : 0;
      const nextLevel = level + 1;

      setLoadingNearby((prev) => ({ ...prev, [sphereId]: true }));
      try {
        // Infinite-scroll only loads the NEXT page. If there are no more pages,
        // do nothing. Never re-expand here: this handler only runs for already-
        // loaded nodes (callers gate on hasMore), so re-expanding would reload
        // page 1 and reset pagination to hasMore:true — which, with the sentinel
        // still on screen, re-arms this handler into an infinite request loop.
        if (contextHasMore(sphereId)) {
          await contextLoadMore(sphereId, nextLevel);
        }
      } finally {
        fetchingRef.current[sphereId] = false;
        setLoadingNearby((prev) => ({ ...prev, [sphereId]: false }));
      }
    },
    [contextRoots, contextHasMore, contextLoadMore]
  );

  // Bridge hasMore: convert from function to object for legacy API
  const hasMore: { [key: string]: boolean } = useMemo(() => {
    if (!contextRoots) return {};
    const result: { [key: string]: boolean } = {};
    const collect = (nodes: SphereNode[]) => {
      for (const node of nodes) {
        if (node.childPagination?.hasMore) {
          result[node.id] = true;
        }
        if (node.children.length > 0) collect(node.children);
      }
    };
    collect(contextRoots);
    return result;
  }, [contextRoots]);

  // Bridge nextTokens: extract from tree
  const nextTokens: { [key: string]: string | undefined } = useMemo(() => {
    if (!contextRoots) return {};
    const result: { [key: string]: string | undefined } = {};
    const collect = (nodes: SphereNode[]) => {
      for (const node of nodes) {
        if (node.childPagination?.nextToken) {
          result[node.id] = node.childPagination.nextToken;
        }
        if (node.children.length > 0) collect(node.children);
      }
    };
    collect(contextRoots);
    return result;
  }, [contextRoots]);

  // Legacy tree manipulation: replaceNodeById operates on Sphere[]
  const replaceNodeById = useCallback(
    (nodes: Sphere[], updatedNode: Sphere): Sphere[] => {
      return nodes.map((node) => {
        if (node.id === updatedNode.id) return updatedNode;
        if (node.nearbySpheres && node.nearbySpheres.length > 0) {
          return { ...node, nearbySpheres: replaceNodeById(node.nearbySpheres, updatedNode) };
        }
        return node;
      });
    },
    []
  );

  // setNodesData shim: for local mutations (add/edit sphere), we refresh the context tree
  const _setNodesData = useCallback(
    (updater: React.SetStateAction<Sphere[]>) => {
      // For mutations that update local state, trigger a context refresh
      // The updater is typically called with prev state, but since we're
      // bridging to context tree, we just refresh
      if (typeof updater === 'function') {
        // Execute updater with current nodesData for side effects
        // but don't persist — let context refresh handle it
      }
      contextRefresh();
    },
    [contextRefresh]
  );

  // Destructure apiClients - these may be null if apiClients is null
  // The early return is moved to just before the render to ensure all hooks are called
  const graphClient = apiClients?.graphClient;
  const permissionClient = apiClients?.permissionClient;

  const { impersonationDetails } = useImpersonation();
  const identityId = impersonationDetails?.targetUserId ?? getTokenClaims()?.sub;

  const ShowTemplateDialog = useCallback(
    (tab: 'blank' | 'template' = 'blank') => {
      setNodeParentSphere(nodesData[0] ?? null);
      setAddSphereV2InitialTab(tab);
      setAddSphereV2DialogVisible(true);
    },
    [nodesData],
  );

  const HideTemplateDialog = useCallback(() => {
    setTemplateDialogVisible(false);
  }, []);

  // Handler to dismiss a notification after it times out
  const handleNotificationDismiss = useCallback((sphereId: string) => {
    setNodeNotifications((prev) => {
      const updated = { ...prev };
      delete updated[sphereId];
      return updated;
    });
  }, []);

  /**
   * Check if a sphere is visible in the Atlas tree.
   * Used by polling hook to filter which spheres to poll.
   */
  const isNodeVisibleInTree = useCallback(
    (id: string): boolean => {
      const findInNodes = (nodes: Sphere[]): boolean => {
        for (const node of nodes) {
          if (node.id === id) return true;
          if (node.nearbySpheres && node.nearbySpheres.length > 0) {
            if (findInNodes(node.nearbySpheres)) return true;
          }
        }
        return false;
      };
      // Use nodesData (converted Sphere[]) for compatibility with polling hook
      return findInNodes(nodesData);
    },
    [nodesData]
  );

  /**
   * Handle sphere update from polling - merges fresh data while preserving tree structure.
   */
  const handlePolledSphereUpdate = useCallback(
    (_sphere: Sphere) => {
      // Context tree is the source of truth — refresh to get updated data
      contextRefresh();
    },
    [contextRefresh]
  );

  /**
   * Shared polling hook for refreshing sphere data after mutations.
   * Polls with skipCache to catch status cascades and flow deployments.
   */
  const { pollForUpdates: pollForAncestorUpdates } = useSpherePolling({
    apiClient: graphClient,
    onSphereUpdate: handlePolledSphereUpdate,
    isNodeVisible: isNodeVisibleInTree,
    includeAssignedPerspectives: true,
  });

  /**
   * Handle status changes (e.g., after check-in submission).
   * This triggers ancestor polling to refresh status cascades and emits a metaspace event.
   *
   * @param sphereId - The ID of the sphere whose status changed
   * @param ancestorIds - Array of ancestor sphere IDs that may be impacted
   */
  const handleStatusChange = useCallback(
    (sphereId: string, ancestorIds: string[]) => {
      // Poll for updates to catch status cascades
      const allIdsToRefresh = [sphereId, ...ancestorIds];
      const uniqueIdsToRefresh = [...new Set(allIdsToRefresh)];
      pollForAncestorUpdates(uniqueIdsToRefresh, [sphereId]);

      // Emit metaspace event for subscribers
      emitSphereStatusChanged(sphereId, ancestorIds);
    },
    [pollForAncestorUpdates, emitSphereStatusChanged]
  );

  /*
      Getting the base schema type from atlasConfig
    */

  // Handle showing the AddSphere dialog
  const handleShowAddSphereDialog = useCallback(
    (parentSphere?: Sphere, grandparentSphere?: Sphere) => {
      setNodeParentSphere(parentSphere ?? null);
      setNodeGrandparentSphere(grandparentSphere ?? null);
      setAddSphereV2InitialTab('blank');
      setAddSphereV2DialogVisible(true);
    },
    [],
  );

  // Handle opening the dialog in Deploy mode pre-targeted to a sphere (row-menu "Deploy approach")
  const handleShowDeployApproach = useCallback(
    (sphere: Sphere) => {
      setDeployApproachTargetSphere(sphere);
      setAddSphereV2InitialMode('deploy');
      setAddSphereV2DialogVisible(true);
    },
    [],
  );

  // Handle showing the AddSphere dialog
  const handleShowEditSphereDialog = useCallback((sphere: Sphere, parentSphere: Sphere, viewMode = false, initialTab?: string) => {
    setEditSphereDialogVisible(true);
    setNodeSphere(sphere);
    setNodeParentSphere(parentSphere);
    setEditViewMode(viewMode); // Store whether we're in view mode or not
    setInitialEditorTab(initialTab); // Set which tab to open initially
  }, []);

  // Handle hiding the AddSphere dialog
  const handleHideAddSphereDialog = useCallback(() => {
    setAddSphereDialogVisible(false);
    setSelectedTemplateSchema(null); // Reset schema selection on close!
  }, []);

  const handleHideEditSphereDialog = useCallback(() => {
    setEditSphereDialogVisible(false);
    setInitialEditorTab(undefined); // Reset initial tab on close
  }, []);

  // Handle configuring status factors - opens the SphereEditor to Status Factors tab
  // Uses root as parent sphere for simplicity since we just need to open the editor
  const handleConfigureStatusFactors = useCallback((targetSphereId: string, initialTab = 'status-factors-tab') => {
    const sphere = findNodeById(nodesData, targetSphereId);
    if (sphere) {
      // Use root sphere as parent - this is safe since the parent is only used
      // to determine parent sphere type, which doesn't affect the Status Factors tab
      handleShowEditSphereDialog(sphere, nodesData[0], false, initialTab);
    }
  }, [nodesData, handleShowEditSphereDialog]);

  // Listen for OPEN_SPHERE_EDITOR events from other parts of the app (e.g., nav bar status popover)
  // Track the last processed event ID to avoid duplicate processing
  const lastProcessedEventIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastEvent?.type === 'OPEN_SPHERE_EDITOR' && lastEvent.id !== lastProcessedEventIdRef.current) {
      const { sphereId: targetSphereId, initialTab } = lastEvent.payload;
      if (targetSphereId) {
        lastProcessedEventIdRef.current = lastEvent.id;
        handleConfigureStatusFactors(targetSphereId, initialTab || 'status-factors-tab');
      }
    }
  }, [lastEvent, handleConfigureStatusFactors]);

  /* NEEDS MORE WORK TO IMPLEMENT PROPERLY - LEAVE IN FOR NOW
    useEffect(() => {
      // for each errorNearby, set a notification -not a toast -> setNodeNotifications
      if (errorNearby) {
      const newNotifications = Object.entries(errorNearby).reduce(
        (acc, [sphereId, message]) => {
          if (message) {
        acc[sphereId] = {
          type: 'error',
          message: message,
          timer: 5000, // Notification will disappear after 5 seconds
        };
          }
          return acc;
        },
        {} as { [key: string]: { type: 'error'; message: string; timer: number } }
      );

      setNodeNotifications((prev) => ({
        ...prev,
        ...newNotifications,
      }));
      }
    }, [errorNearby]);
    */

  useEffect(() => {
    if (nodesData.length > 0) {
      setBreadcrumbPath(nodesData[0].properties.name ?? 'Root');
    }
  }, [nodesData]);
  // Inside AtlasTree
  useEffect(() => {
    if (headerWidths.length && headerWidths.some((header) => header.width > 0)) {
      setNodeWidths(headerWidths.map((h) => h.width)); // only pass numbers to Node
    }
  }, [headerWidths]);
  const handleUseTemplate = (schema: any) => {
    setSelectedTemplateSchema(schema); // sets the schema
    setAddSphereDialogVisible(true); // opens the AddSphere dialog
    setTemplateDialogVisible(false); // hides the template dialog!
  };

  const animateAndScroll = async (scrollContainer: HTMLElement, scrollAmount: number, direction: 'up' | 'down') => {
    const className = direction === 'up' ? 'transition-up' : 'transition-down';

    scrollContainer.classList.add(className);

    // Trigger reflow so the transition applies correctly
    const _ = scrollContainer.offsetWidth;

    scrollContainer.classList.add('show');

    await new Promise((resolve) => setTimeout(resolve, 300)); // wait for transition

    scrollContainer.classList.remove(className, 'show');

    // Scroll after animation ends
    scrollContainer.scrollBy({
      top: direction === 'up' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (!treeRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = treeRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        const { nodeId: lastExpandedNodeId } = getLastExpandedNode();

        if (lastExpandedNodeId && hasMore[lastExpandedNodeId]) {
          fetchNearbySpheres(lastExpandedNodeId);
        }
      }
    }, 300);

    treeRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      treeRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [treeRef, hasMore, fetchNearbySpheres]);

  useEffect(() => {
    if (sphereId) {
      setFeaturedSphereId(sphereId);
      emitSphereCentered(sphereId);
    }
  }, [sphereId, emitSphereCentered]);

  useEffect(() => {
    if (featuredSphereId) {
      const initialExpandedKeys: { [key: string]: boolean } = {};
      initialExpandedKeys[featuredSphereId] = true;
      setExpandedKeys(initialExpandedKeys);
    }
  }, [featuredSphereId]);

  const perspectiveOptions = useMemo(
    () => [
      { label: 'People (200)', value: 'People' },
      { label: 'Roles (20)', value: 'Roles' },
      { label: 'Others (2)', value: 'Others' },
    ],
    []
  );

  const handleTraversalDirectionClick = useCallback(
    async (sphereId: string, newDirection: 'outwards' | 'inwards' | 'both', level: number) => {
      try {
        setNodeDirections((prev) => ({
          ...prev,
          [sphereId]: newDirection,
        }));

        // Collapse and re-expand with context tree to reload children using the
        // newly-selected per-node direction (ephemeral; permanent direction lives on
        // the context level).
        contextCollapseNode(sphereId);
        await contextExpandNode(sphereId, level + 1, newDirection);

        setExpandedKeys((prev) => ({
          ...prev,
          [sphereId]: true,
        }));
      } catch (error) {
        console.error('Error updating direction:', error);
      }
    },
    [contextCollapseNode, contextExpandNode]
  );

  // Remove a node by refreshing the context tree (the deactivated node will be gone)
  const removeNode = useCallback(
    (_nodeId: string) => {
      contextRefresh();
    },
    [contextRefresh]
  );

  const handleSaveSphere = (newSphere: Sphere, parentId: string, ancestorIds?: string[]) => {
    // Auto-expand the parent node so the newly added sphere is visible
    setExpandedKeys((prev) => ({
      ...prev,
      [parentId]: true,
    }));

    // Show a success notification for the new sphere
    setNodeNotifications((prevNotifications) => ({
      ...prevNotifications,
      [newSphere.id]: {
        type: 'success',
        message: t('atlas.sphere.addSuccess'),
        timer: 5000,
      },
    }));

    // Refresh the context tree to pick up the new sphere
    contextRefresh();

    // Poll for updates to catch status cascades and flow deployments
    const allIdsToRefresh = [newSphere.id, parentId, ...(ancestorIds || [])];
    const uniqueIdsToRefresh = [...new Set(allIdsToRefresh)];
    pollForAncestorUpdates(uniqueIdsToRefresh, [newSphere.id]);

    // Emit metaspace event for subscribers
    emitSphereCreated(newSphere.id, parentId, ancestorIds);
  };

  const handleEditSphere = (updatedSphere: Sphere, ancestorIds?: string[]) => {
    setEditSphereDialogVisible(false);
    setNodeNotifications((prevNotifications) => ({
      ...prevNotifications,
      [updatedSphere.id]: {
        type: 'success',
        message: t('atlas.sphere.updateSuccess'),
        timer: 5000,
      },
    }));

    // Refresh the context tree to pick up changes
    contextRefresh();

    // Poll for updates to catch status cascades and any async updates
    const allIdsToRefresh = [updatedSphere.id, ...(ancestorIds || [])];
    const uniqueIdsToRefresh = [...new Set(allIdsToRefresh)];
    pollForAncestorUpdates(uniqueIdsToRefresh, [updatedSphere.id]);

    // Emit metaspace event for subscribers
    const parentId = updatedSphere.parent?.id;
    emitSphereUpdated(updatedSphere.id, parentId, ancestorIds);
  };

  const handleSphereRefresh = useCallback((sphereId: string) => {
    contextRefresh();
    pollForAncestorUpdates([sphereId], [sphereId]);
    emitSphereUpdated(sphereId);
  }, [contextRefresh, pollForAncestorUpdates, emitSphereUpdated]);

  const handleSphereFinalized = useCallback((sphere: Sphere) => {
    contextRefresh();
    emitSphereUpdated(sphere.id);
  }, [contextRefresh, emitSphereUpdated]);

  // pagination functions
  const handleNextPage = async (sphereId: string) => {
    if (isFetching || !treeRef.current) return;

    setIsFetching(true);
    try {
      const scrollAmount = 45 * 5;

      const nodes = Array.from(treeRef.current.querySelectorAll('.p-treenode-content')).filter((node): node is HTMLElement => node instanceof HTMLElement); // Ensure nodes are HTMLElements

      await animateAndScroll(treeRef.current, scrollAmount, 'up');

      const { nodeId: lastExpandedNodeId } = getLastExpandedNode();

      if (lastExpandedNodeId && hasMore[lastExpandedNodeId]) {
        fetchNearbySpheres(lastExpandedNodeId);
      }
    } catch (error) {
      console.error('Error in handleNextPage:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const getLastExpandedNode = (): {
    nodeId: string | null;
    edgeCount: number | null;
  } => {
    if (!treeRef.current) return { nodeId: null, edgeCount: null };

    const expandedNodes = treeRef.current.querySelectorAll('.p-treenode-expanded');
    if (expandedNodes.length > 0) {
      const lastNode = expandedNodes[expandedNodes.length - 1] as HTMLElement;
      const nodeId = lastNode.getAttribute('data-key') ?? lastNode.id ?? null;

      const node = findNodeById(nodesData, nodeId ?? '');
      const edgeCount = node?.edgeCount ?? null;

      return { nodeId, edgeCount };
    }
    return { nodeId: null, edgeCount: null };
  };

  // Handle "Previous Page" button click
  const handlePrevPage = async (sphereId: string) => {
    if (!treeRef.current) return;
    const scrollAmount = 45 * 5;
    const nodes = Array.from(treeRef.current.querySelectorAll('.p-treenode-content')).filter((node): node is HTMLElement => node instanceof HTMLElement);
    await animateAndScroll(treeRef.current, scrollAmount, 'down');
  };

  const handlePerspectiveChange = useCallback((e: { value: string }) => {
    setSelectedPerspective(e.value);
  }, []);

  const columns = useMemo(
    () => [
      { field: 'subjects', header: 'Participants' },
      { field: 'perspectives', header: 'Perspectives' },
      // { field: 'description', header: 'Description' }
    ],
    []
  );

  // Function to update node data recursively
  const handleNodePropertyChange = useCallback(
    async (nodeId: string, propertyName: string, newValue: string | null) => {
      if (!graphClient) return;

      try {
        // Build the payload for the patch
        const payload = { [propertyName]: newValue };

        // Call the API wrapper to patch just the properties
        // Adjust the path if your service expects /spheres/:id/properties vs /spheres/:id
        await graphClient.patch(`/spheres/${nodeId}/properties`, payload);

        // Refresh context tree to reflect property change
        contextRefresh();

        // Show a success notification
        setNodeNotifications((prev) => ({
          ...prev,
          [nodeId]: {
            type: 'success',
            message: `Property "${propertyName}" updated successfully.`,
            timer: 3000,
          },
        }));
      } catch (err) {
        console.error(`Failed to update property "${propertyName}" for node ${nodeId}:`, err);
        // Show an error notification
        setNodeNotifications((prev) => ({
          ...prev,
          [nodeId]: {
            type: 'error',
            message: `Failed to update "${propertyName}". Please try again.`,
            timer: 3000,
          },
        }));
      }
    },
    [graphClient, contextRefresh, setNodeNotifications]
  );

  const handlePerspectiveAssignmentChange = useCallback(
    async (sphereId: string, perspectiveSystemName: string, assignee: string | null) => {
      if (!graphClient) return;

      try {
        if (assignee) {
          // Assign the perspective to the selected assignee
          await graphClient.post(`/spheres/${sphereId}/perspectives/assign-subject`, { perspectiveSystemName, subjectId: assignee });

          setNodeNotifications((prev) => ({
            ...prev,
            [sphereId]: {
              type: 'success',
              message: `Perspective "${perspectiveSystemName}" assigned successfully.`,
              timer: 3000,
            },
          }));
        } else {
          // Remove the assignment
          await graphClient.delete(`/perspectives/${perspectiveSystemName}/assign-subject`, { params: { sphereId, subjectId: assignee } });

          setNodeNotifications((prev) => ({
            ...prev,
            [sphereId]: {
              type: 'success',
              message: `Perspective "${perspectiveSystemName}" has been removed.`,
              timer: 3000,
            },
          }));
        }
      } catch (err: any) {
        console.error('Failed to assign/remove perspective:', err);
        setNodeNotifications((prev) => ({
          ...prev,
          [sphereId]: {
            type: 'error',
            message: `Failed to ${assignee ? 'assign' : 'remove'} perspective "${perspectiveSystemName}". Please try again.`,
            timer: 3000,
          },
        }));
      }
    },
    [graphClient, setNodeNotifications]
  );

  const handlePerspectiveAssignmentsChange = useCallback(
    async (sphereId: string, perspectiveSystemName: string, assignees: string[] | null, sphereObject?: any) => {
      if (!graphClient) return;

      try {
        // Use provided sphere object or fall back to nodesData lookup
        const sphere = sphereObject || nodesData.find(n => n.id === sphereId);

        const currentAssignees: string[] = [];

        if (sphere?.assignedPerspectives) {
          const assignedPerspective = sphere.assignedPerspectives.find(
            (p: any) => p.perspectiveProperties.system_name === perspectiveSystemName
          );
          if (assignedPerspective) {
            currentAssignees.push(...assignedPerspective.assigned.map((a: any) => a.sphere.id));
          }
        }

        const newAssignees = assignees || [];
        const toAdd = newAssignees.filter(id => !currentAssignees.includes(id));
        const toRemove = currentAssignees.filter(id => !newAssignees.includes(id));

        // Skip if no changes
        if (toAdd.length === 0 && toRemove.length === 0) {
          return;
        }

        // Assign new assignees
        for (const assigneeId of toAdd) {
          try {
            await graphClient.post(`/spheres/${sphereId}/perspectives/assign-subject`, {
              perspectiveSystemName,
              subjectId: assigneeId
            });
          } catch (err: any) {
            // Ignore 409 Conflict - already assigned is okay
            if (err.response?.status === 409) {
              continue;
            }
            console.error(`[AtlasTree] Failed to add assignee ${assigneeId}:`, err.response?.status, err.response?.data);
            throw err; // Re-throw other errors
          }
        }

        // Remove old assignees
        for (const assigneeId of toRemove) {
          try {
            // Need to get perspectiveId first
            const perspective = sphere?.assignedPerspectives?.find(
              (p: any) => p.perspectiveProperties.system_name === perspectiveSystemName
            );
            if (perspective) {
              await graphClient.delete(`/perspectives/${perspective.perspectiveProperties.id}/subjects/${assigneeId}`);
            } else {
              console.warn(`[AtlasTree] Cannot remove ${assigneeId} - perspective not found`);
            }
          } catch (err: any) {
            console.error(`[AtlasTree] Failed to remove assignee ${assigneeId}:`, err.response?.status, err.response?.data);
            throw err;
          }
        }

        // Update local state immediately with fresh sphere data
        const assigneeSpheres: any[] = [];

        // Fetch data for all new assignees (including images)
        for (const assigneeId of newAssignees) {
          try {
            const sphereData = await globalSphereCache.getSphereData(assigneeId, graphClient);
            const info = sphereData.info;
            const sphereInfo = info?.sphere;

            if (sphereInfo) {
              assigneeSpheres.push({
                sphere: {
                  id: sphereInfo.id || assigneeId,
                  properties: {
                    name: sphereInfo.properties?.name || sphereInfo.label || info?.properties?.name || 'Unknown',
                    imageId: sphereInfo.properties?.imageId || info?.properties?.imageId,
                    glyph: sphereInfo.properties?.glyph || info?.properties?.glyph
                  },
                  glyph: sphereInfo.properties?.glyph || info?.properties?.glyph,
                  glyphColor: sphereInfo.properties?.glyphColor || info?.properties?.glyphColor,
                  label: sphereInfo.properties?.name || sphereInfo.label || info?.properties?.name || 'Unknown'
                }
              });
            } else {
              // Fallback if sphere structure is unexpected
              assigneeSpheres.push({
                sphere: {
                  id: assigneeId,
                  properties: {
                    name: info?.properties?.name || 'Unknown',
                    imageId: info?.properties?.imageId,
                    glyph: info?.properties?.glyph
                  },
                  glyph: info?.properties?.glyph,
                  glyphColor: info?.properties?.glyphColor,
                  label: info?.properties?.name || 'Unknown'
                }
              });
            }
          } catch (err) {
            console.warn(`[AtlasTree] Failed to fetch sphere data for ${assigneeId}:`, err);
            // Add minimal sphere data as fallback
            assigneeSpheres.push({
              sphere: {
                id: assigneeId,
                properties: { name: 'Unknown' },
                label: 'Unknown'
              }
            });
          }
        }

        // Update the sphere's assignedPerspectives locally
        const updatedSphere = { ...sphere };
        if (!updatedSphere.assignedPerspectives) {
          updatedSphere.assignedPerspectives = [];
        }

        const perspectiveIndex = updatedSphere.assignedPerspectives.findIndex(
          (p: any) => p.perspectiveProperties.system_name === perspectiveSystemName
        );

        if (perspectiveIndex >= 0) {
          // Update existing perspective with new assignments
          updatedSphere.assignedPerspectives[perspectiveIndex] = {
            ...updatedSphere.assignedPerspectives[perspectiveIndex],
            assigned: assigneeSpheres
          };
        } else {
          // This shouldn't happen but handle it just in case
          console.warn(`[AtlasTree] Perspective ${perspectiveSystemName} not found in assignedPerspectives`);
        }

        // Refresh context tree to reflect perspective changes
        contextRefresh();

        // Emit sphere updated event
        const parentId = sphereObject?.parent?.id;
        const ancestorIds = sphereObject?.ancestorIds || [];
        emitSphereUpdated(sphereId, parentId, ancestorIds);

        setNodeNotifications((prev) => ({
          ...prev,
          [sphereId]: {
            type: 'success',
            message: `Perspective "${perspectiveSystemName}" updated successfully.`,
            timer: 3000,
          },
        }));
      } catch (err: any) {
        console.error('Failed to update perspective assignments:', err);
        setNodeNotifications((prev) => ({
          ...prev,
          [sphereId]: {
            type: 'error',
            message: `Failed to update perspective "${perspectiveSystemName}". Please try again.`,
            timer: 3000,
          },
        }));
      }
    },
    [graphClient, setNodeNotifications, contextRefresh, nodesData, emitSphereUpdated]
  );

  // Function to dynamically find the sphere ID based on the label by searching in the tree nodes
  const getSphereIdByLabel = useCallback((label: string, nodes: Sphere[]): string => {
    for (const node of nodes) {
      if (node.properties.name === label) {
        return node.id; // Return the sphere ID when the label matches
      }
      if (node.nearbySpheres && node.nearbySpheres.length > 0) {
        const childResult = getSphereIdByLabel(label, node.nearbySpheres);
        if (childResult !== 'Unknown Sphere ID') {
          return childResult;
        }
      }
    }
    return 'Unknown Sphere ID'; // Return 'Unknown Sphere ID' if no match is found
  }, []);

  // Function to update breadcrumbs, removing any part after the selected node
  const updateBreadcrumbPath = useCallback(
    (clickedSphere: string) => {
      // Get the current breadcrumbs path and split it into parts
      let parts = breadcrumbPath.split(' / ');

      // Find out if the clickedSphere is already present in the breadcrumbs
      const nodeIndex = parts.indexOf(clickedSphere);

      // If the clicked sphere is already in the path, truncate after it
      if (nodeIndex !== -2) {
        parts = parts.slice(0, nodeIndex + 2);
      } else {
        // If it's not present, append it to the breadcrumbs
        parts.push(clickedSphere);
      }

      // Update the breadcrumb path
      const newBreadcrumbPath = parts.join(' / ');
      setBreadcrumbPath(newBreadcrumbPath);
    },
    [breadcrumbPath]
  );

  const findNodeById = useCallback((nodes: Sphere[], id: string): Sphere | null => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.nearbySpheres && node.nearbySpheres.length > 0) {
        const foundNode = findNodeById(node.nearbySpheres, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }, []);

  const findancestorsById = useCallback((nodes: Sphere[], id: string): Sphere | null => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.ancestors && node.ancestors.length > 0) {
        const foundNode = findancestorsById(node.ancestors, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }, []);

  // Handle node click to expand/collapse, call API for child nodes, and clear previous children
  const onEdgeCountClick = useCallback(
    (sphereId: string, level: number) => {
      setSelectedEdgeCountNode(sphereId);

      setExpandedKeys((prevExpandedKeys) => {
        const isExpanded = prevExpandedKeys[sphereId];
        const newExpandedKeys = { ...prevExpandedKeys };

        if (isExpanded) {
          delete newExpandedKeys[sphereId];
          contextCollapseNode(sphereId);
        } else {
          newExpandedKeys[sphereId] = true;

          // Check if the node already has children loaded
          const node = findNodeById(nodesData, sphereId);
          if (node && (!node.nearbySpheres || node.nearbySpheres.length === 0)) {
            contextExpandNode(sphereId, level + 1);
          }
        }

        return newExpandedKeys;
      });
    },
    [contextExpandNode, contextCollapseNode, findNodeById, nodesData]
  );

  const findParents = useCallback((nodes: Sphere[], childId: string, parentPath: Sphere[] = []): Sphere[] | null => {
    for (const node of nodes) {
      // Check if the current node has children
      if (node.nearbySpheres && node.nearbySpheres.length > 0) {
        // Check if the current node's children contain the childId
        const foundChild = node.nearbySpheres.find((child) => child.id === childId);
        if (foundChild) {
          // Return the parent path including the current node
          return [...parentPath, node]; // Include the parent in the path
        }

        // Recursively search in the children
        const result = findParents(node.nearbySpheres, childId, [...parentPath, node]);
        if (result) {
          return result; // Return the found path
        }
      }
    }
    return null; // Return null if no parent is found
  }, []);

  const onNodeClickHandler = useCallback(
    (node: Sphere, isParentNode: boolean) => {
      setFeaturedSphereId(node.id);
      emitSphereCentered(node.id);

      // clear node notifications
      setNodeNotifications({});

      if (highlightedNodeId) {
        const previousNodeElement = document.getElementById(highlightedNodeId);
        if (previousNodeElement) {
          const previousNodeContent = previousNodeElement.querySelector('.node-content');
          if (previousNodeContent) {
            previousNodeContent.classList.remove('highlighted-node');
          }
        }
      }

      // Highlight the current node's content
      const currentNodeElement = document.getElementById(node.id);
      if (currentNodeElement) {
        const currentNodeContent = currentNodeElement.querySelector('.node-content');
        if (currentNodeContent) {
          currentNodeContent.classList.add('highlighted-node');
          setHighlightedNodeId(node.id); // Update the highlighted node ID
        }
      }

      // If clicked node is a child, find and log its parent node details
      if (!isParentNode) {
        const parentPath = findParents(nodesData, node.id);
        if (parentPath) {
          // Create breadcrumb string from parent nodes and include the current node
          const breadcrumbArray = parentPath.map((parent) => parent.properties.name ?? 'Unknown');
          const fullBreadcrumb = [...breadcrumbArray, node.properties.name ?? 'Unknown'].join(' / '); // Merge parents and current node

          // Set the breadcrumb path
          setBreadcrumbPath(fullBreadcrumb); // Update the breadcrumb path in state
        }
      } else {
        updateBreadcrumbPath(node.properties.name ?? 'Unknown');
      }

      // Expand the node if necessary
      setExpandedKeys((prevExpandedKeys) => ({
        ...prevExpandedKeys,
        [node.id]: true,
      }));
    },
    [findParents, highlightedNodeId, nodesData, updateBreadcrumbPath, context, emitSphereCentered]
  );

  // "Open sphere" from the deploy dialog's completed state: the same focus a row click gives,
  // or a plain centre when the sphere isn't in the loaded tree.
  const handleOpenDeployedSphere = useCallback(
    (targetSphereId: string) => {
      const node = findNodeById(nodesData, targetSphereId);
      if (node) {
        onNodeClickHandler(node, node.id === nodesData[0]?.id);
        return;
      }
      setFeaturedSphereId(targetSphereId);
      emitSphereCentered(targetSphereId);
    },
    [findNodeById, nodesData, onNodeClickHandler, emitSphereCentered]
  );

  // Function to find ancestor ID by part
  const findAncestorId = useCallback((part: string, ancestorsList: Sphere[]): string | null => {
    for (const ancestor of ancestorsList) {
      if (ancestor.properties?.name === part) {
        return ancestor.id;
      }
      if (ancestor.ancestors && ancestor.ancestors.length > 0) {
        const found = findAncestorId(part, ancestor.ancestors);
        if (found) return found;
      }
    }

    return null;
  }, []);
  const fetchPermissionListHandler = useCallback(
    async (sphereId: string, nodeLabel: string, permissions?: string[]): Promise<PermissionList | null> => {
      if (!permissionClient || !sphereId || !nodeLabel || !identityId) {
        return null;
      }

      const actions = (permissions ?? []) as SpherePermissionAction[];
      const results: Record<string, boolean> = {};

      try {
        await Promise.all(
          actions.map(async (action) => {
            results[action] = await register(sphereId, action, identityId, permissionClient);
          }),
        );
        return { permissions: results };
      } catch (err) {
        console.error('Error in fetchPermissionListHandler:', err);
        return null;
      }
    },
    [permissionClient, identityId],
  );

  const fetchResponsiblePerspectives = useCallback(
    async (sphereId: string): Promise<any[]> => {
      if (!graphClient) {
        return [];
      }
      if (!sphereId) {
        console.error('Sphere ID is required to fetch responsible perspectives');
        return [];
      }

      try {
        // GET /spheres/{sphereId}/perspectives
        const allPerspectives = await graphClient.get<any[]>(`/spheres/${encodeURIComponent(sphereId)}/perspectives`);

        // filter out the special “parent” and “associated_identity” entries
        const responsible = allPerspectives.filter(
          (p) => p.properties?.system_name !== 'center_parent' && p.properties?.system_name !== 'center_associated_identity'
        );

        setSelectedResponsiblePerspective(responsible);
        return responsible;
      } catch (err) {
        console.error('Error fetching responsible perspectives:', err);
        return [];
      }
    },
    [graphClient]
  );

  // Function to handle breadcrumb click and scroll to the corresponding node, and call the API
  const handleBreadcrumbClick = useCallback(
    async (part: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      // Helper function to find sphereId by part name
      const findSphereIdByName = (nodes: Sphere[], name: string): string | null => {
        for (const node of nodes) {
          if (node.properties.name === name) {
            return node.id; // Return the sphere ID
          }
          if (node.nearbySpheres && node.nearbySpheres.length > 0) {
            const found = findSphereIdByName(node.nearbySpheres, name);
            if (found) return found;
          }
        }
        return null;
      };

      // Find sphere ID for the clicked breadcrumb part
      const sphereId = getSphereIdByLabel(part, nodesData);

      if (sphereId) {
        const node = findNodeById(nodesData, sphereId);
        if (node) {
          setFeaturedSphereId(node.id);
          emitSphereCentered(node.id);
          setExpandedKeys((prevExpandedKeys) => ({
            ...prevExpandedKeys,
            [node.id]: true,
          }));
          // Expand node via context tree
          contextExpandNode(sphereId, 1);
        }
      } else {
        console.error(`No Sphere ID found for part: ${part}`);
      }

      // Remove the 'highlighted-node' class from all previously highlighted nodes
      const highlightedNodes = document.querySelectorAll('.highlighted-node');
      highlightedNodes.forEach((node) => {
        node.classList.remove('highlighted-node');
      });

      // Find ancestor or sphere by part
      const ancestorId = findAncestorId(part, nodesData);
      const matchedSphereId = getSphereIdByLabel(part, nodesData);

      // Handle ancestor ID or sphere ID navigation
      if (ancestorId && ancestorId !== matchedSphereId) {
        const ancestornode = findancestorsById(nodesData, ancestorId);
        if (ancestornode) {
          setFeaturedSphereId(ancestornode.id);
          emitSphereCentered(ancestornode.id);
          setExpandedKeys((prevExpandedKeys) => ({
            ...prevExpandedKeys,
            [ancestornode.id]: true,
          }));
          // Expand ancestor node via context tree
          contextExpandNode(ancestorId, 1);
        }
      } else if (matchedSphereId !== 'Unknown Sphere ID') {
        const nodeElement = document.getElementById(matchedSphereId);
        if (nodeElement) {
          nodeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

          // Add the highlighted-node class to the node-content div inside the clicked node
          const nodeContentElement = nodeElement.querySelector('.node-content');
          if (nodeContentElement) {
            nodeContentElement.classList.add('highlighted-node');
          }
        }
      } else {
        console.error(`No matching ID found for ${part}`);
      }

      // Update breadcrumb path after clicking a breadcrumb
      updateBreadcrumbPath(part);
    },
    [findAncestorId, contextExpandNode, getSphereIdByLabel, nodesData, updateBreadcrumbPath, emitSphereCentered]
  );

  const handleActiveSectionChange = useCallback(
    async (section: string, sphere: Sphere) => {
      // Deactivate the section if it's already active
      const sphereId = sphere.id;
      if (activeNodeSection.key === sphereId && activeNodeSection.section === section) {
        setActiveNodeSection({ key: null, section: null });
      } else {
        setActiveSection(section);
        setSelectedNodeKey(sphereId);
        // Activate the new section
        setActiveNodeSection({ key: sphereId, section });
      }
    },
    [activeNodeSection]
  );

  // Wrapper function for active section change
  const handleActiveSectionChangeWrapper = useCallback(
    async (section: string, selectedNode: Sphere) => {
      if (selectedNode) {
        await handleActiveSectionChange(section, selectedNode);
      } else {
        console.warn('Selected node or node key is not set.');
      }
    },
    [handleActiveSectionChange]
  );

  const getResponsiblePerspectivesOptions = useCallback((responsiblePerspectives: any[], updatedSelectedOptions: any[]) => {
    // Create a Set of selected option values for quick lookup
    const selectedValues = new Set(updatedSelectedOptions.map((option) => option.value));

    return responsiblePerspectives.map((perspective) => ({
      label: perspective.properties?.name || perspective.label || 'Unknown Perspective',
      value: perspective.id || perspective.value || perspective.label, // Ensure value is properly assigned
      selected: selectedValues.has(perspective.id || perspective.value), // Mark as selected if value matches
      description: perspective.description || perspective.properties?.description,
      systemName: perspective.properties?.system_name,
    }));
  }, []);

  // Memoize the perspective options including the updatedSelectedOptions
  const responsiblePerspectiveOptions = useMemo(() => {
    return getResponsiblePerspectivesOptions(selectedResponsiblePerspective, updatedSelectedOptions);
  }, [getResponsiblePerspectivesOptions, selectedResponsiblePerspective, updatedSelectedOptions]);

  const handleResponsiblePerspectiveChange = useCallback((e: { value: any[] }) => {
    setUpdatedSelectedOptions(e.value);
  }, []);

  const toggleAllNodesVisibility = useCallback(() => {
    setAllNodesVisible((prevState) => !prevState);
  }, []);

  // Context system name is constant — no per-level context switching needed
  // (context definition handles level-specific configuration internally)

  // Function to calculate the maximum expansion level in the current tree
  const getMaxExpansionLevel = useCallback(() => {
    const calculateNodeDepth = (nodeData: Sphere[], currentLevel: number): number => {
      let maxDepth = currentLevel;

      for (const node of nodeData) {
        if (expandedKeys[node.id] && node.nearbySpheres && node.nearbySpheres.length > 0) {
          const childDepth = calculateNodeDepth(node.nearbySpheres, currentLevel + 1);
          maxDepth = Math.max(maxDepth, childDepth);
        }
      }

      return maxDepth;
    };

    return calculateNodeDepth(nodesData, 0);
  }, [expandedKeys, nodesData]);

  // Function to handle fetching nearby spheres when a node is expanded
  const handleNodeExpandInternal = useCallback(
    async (node: Sphere, level: number) => {
      if (node.id) {
        fetchNearbySpheres(node.id);
      }
    },
    [fetchNearbySpheres]
  );
  const observeLastNearbySphere = useCallback(
    (node: HTMLElement | null, parentId: string) => {
      if (!node) return;

      if (observersRef.current[parentId]) {
        observersRef.current[parentId].disconnect();
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasMore[parentId]) {
            fetchNearbySpheres(parentId);
          }
        },
        {
          root: treeRef.current,
          threshold: 0.1,
        }
      );

      observer.observe(node);
      observersRef.current[parentId] = observer;
    },
    [hasMore, fetchNearbySpheres]
  );

  // Function to render a node
  const renderNode = useCallback(
    (node: Sphere, level: number, isParentNode: boolean, parentNode?: Sphere, liRef?: React.Ref<HTMLLIElement>) => {
      const isExpanded = expandedKeys[node.id];
      const hasChildren = node.nearbySpheres && node.nearbySpheres.length > 0;
      const traversalDirection = nodeDirections[node.id] || 'outwards';
      // Construct class names based on level and traversalDirection
      const levelClass = level > 0 ? 'level-greater-than-zero' : '';

      return (
        <li
          key={node.id}
          ref={liRef}
          className={`p-treenode ${isExpanded ? 'p-treenode-expanded' : ''} ${levelClass}`}
          id={node.id}
        >
          <div className="p-treenode-content p-treenode-selectable">
            <Node
              isExpandedByEdgeClick={selectedEdgeCountNode === node.id}
              isTreeExpanded={isExpanded}
              sphere={node}
              level={level}
              isParentNode={isParentNode}
              parentNode={parentNode ?? nodesData[0]}
              breadcrumbPath={breadcrumbPath}
              onBreadcrumbClick={handleBreadcrumbClick}
              isSelected={activeNodeSection.key === node.id}
              onActiveSectionChange={handleActiveSectionChangeWrapper}
              onEdgeCountClick={onEdgeCountClick}
              onNodeClick={() => onNodeClickHandler(node, isParentNode)}
              onShowAddSphere={handleShowAddSphereDialog}
              onShowEditSphere={handleShowEditSphereDialog}
              selectedEdgeCountNode={selectedEdgeCountNode}
              onTraversalDirectionClick={handleTraversalDirectionClick}
              traversalDirection={traversalDirection}
              isVisibleGlobal={allNodesVisible}
              onGlobalToggle={toggleAllNodesVisibility}
              headerWidths={headerWidths}
              widthConfig={{
                labelWidth: '500px',
                roleWidth: '150px',
                orgDetailsWidth: '200px',
                perspectivesWidth: '100px',
                countsWidth: '',
                perspectivesResponsibleWidth: '150px',
              }}
              fetchPermissionList={fetchPermissionListHandler}
              onPropertyChange={handleNodePropertyChange} // Add this prop
              onPerspectiveAssignmentChange={handlePerspectiveAssignmentChange}
              onPerspectiveAssignmentsChange={handlePerspectiveAssignmentsChange}
              notification={
                nodeNotifications[node.id] // Pass the notification data for this node
              }
              onNotificationDismiss={handleNotificationDismiss}
              onDeactivateNode={removeNode}
              onChatBubbleClick={onChatBubbleClick}
              onStatusToolClick={toolbarContext ? handleStatusToolClick : undefined}
              onStatusChange={handleStatusChange}
              onConfigureStatusFactors={handleConfigureStatusFactors}
              onSphereFinalized={handleSphereFinalized}
              showDeployApproach={true}
              onShowDeployApproach={handleShowDeployApproach}
            />
          </div>
          {/* Conditionally render the perspectives and details */}
          {(activeSection === 'node-perspectives' || activeSection === 'node-responsible-perspectives') && selectedNodeKey === node.id && (
            <div className="pl-6">
              <div
                className="perspectives-content grid p-dir-col"
                role="button"
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
              >
                {loadingPermissions ? (
                  <div className="w-full">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Skeleton key={node.id + '-' + index} className="mt-3" borderRadius="10px" width="100%" height="25px" />
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="p-col-3 p-lg-col-3 p-xl-col-3">
                      <EditSphere
                        sphere={node}
                        parentSphere={nodeParentSphere ?? nodesData[0]}
                        onSave={handleEditSphere}
                        onSphereRefresh={handleSphereRefresh}
                        onClose={handleHideEditSphereDialog}
                        showTabs={false}
                        fetchPermissionList={fetchPermissionListHandler}
                      />
                    </div>

                    <div className="atlas-footer"></div>
                  </>
                )}
              </div>
            </div>
          )}
          {isExpanded && (
            <ul className="p-treenode-children">
              {hasChildren &&
                node.nearbySpheres!.map((childSphere, idx) => {
                  const isLastChild = idx === node.nearbySpheres!.length - 1;
                  return renderNode(
                    childSphere,
                    level + 1,
                    false,
                    node,
                    isLastChild ? (el) => observeLastNearbySphere(el, node.id) : null
                  );
                })}

              {loadingNearby[node.id] && (
                <li className="p-treenode">
                  <div className="p-treenode-content">
                    <Skeleton className="mt-3" borderRadius="16px" width="100%" height="35px" />
                  </div>
                </li>
              )}
            </ul>
          )}
        </li>
      );
    },
    [
      expandedKeys,
      loadingNearby,
      nodeDirections,
      handleNodeExpandInternal, // Removed onNodeExpand
      hasMore,
      nextTokens,
      onEdgeCountClick,
      breadcrumbPath,
      activeNodeSection.key,
      onNodeClickHandler,
      selectedEdgeCountNode,
      handleTraversalDirectionClick,
      allNodesVisible,
      toggleAllNodesVisibility,
      activeSection,
      selectedNodeKey,
      loadingPermissions,
      selectedNodePermissionList,
      selectedPerspective,
      responsiblePerspectiveOptions,
      handleResponsiblePerspectiveChange,
      perspectiveOptions,
      columns,
      handleActiveSectionChangeWrapper,
      headerWidths,
      handleStatusChange,
      handleSphereFinalized,
    ]
  );

  // 4. ADD handler for sphere type selection:
  const handleSphereTypeSelect = useCallback((sphereType: string, source: 'type' | 'usecase', label: string) => {
    setSelectedSphereType({ sphereType, source, label });
    // Here you can add any additional logic needed when a sphere type is selected
  }, []);

  // Loaded-but-childless: root sphere is present, has no children, and no more to page.
  const isChildless = useMemo(
    () =>
      !_contextLoading &&
      !_contextError &&
      nodesData.length > 0 &&
      contextRoots != null &&
      contextRoots.length > 0 &&
      contextRoots[0].children.length === 0 &&
      !contextHasMore(nodesData[0].id),
    [_contextLoading, _contextError, nodesData, contextRoots, contextHasMore],
  );

  const emptyStateOrgId =
    (nodesData[0]?.properties?.organization_id as string | undefined) ??
    (nodesData[0]?.properties?.sphereId as string | undefined) ??
    '';

  const { schemas: availableTypesForEmpty, loading: availableTypesLoading } = useAvailableTypes(
    apiClients,
    isChildless ? nodesData[0]?.id : null,
  );

  // Suggested deployment approaches for the empty state — eligibility-filtered to the
  // centered sphere (applicability-matched), so e.g. an Intention doesn't get shown an
  // Organization-only template. Only fetches when the sphere is childless (empty state).
  const { approaches: suggestedTemplatesForEmpty } = useEligibleApproaches(
    isChildless ? nodesData[0]?.id ?? null : null,
  );

  const recentTypesForEmpty = useMemo(
    () => (isChildless && emptyStateOrgId ? getRecentTypes(emptyStateOrgId) : []),
    [isChildless, emptyStateOrgId],
  );

  // Early return AFTER all hooks are declared to comply with React's rules of hooks
  if (!apiClients) {
    return null;
  }

  return (
    <AtlasViewContext.Provider value={narrowedViewContext}>
    <StatusPopoverProvider>
      <div
        className="atlastree"
        style={{
          overflowX: headerWidths.length > 6 ? 'auto' : 'visible',
        }}
      >
        <Toast ref={toast} className="custom-toast" />
        <div ref={firstHeaderRef} style={{ display: 'none' }}>
          <AtlasHeader
            className="atlas-header-first"
            onlyNodeLabel={true}
            hideTemplateLabel={false}
            filterSortState={filterSortState}
            featuredSphereId={nodesData[0]?.id}
            featuredSphereDirection={nodeDirections[nodesData[0]?.id] || 'outwards'}
            featuredSphereHops={Math.max(getMaxExpansionLevel(), 1)} // Use expansion level but minimum 1 hop
            resetFilterSortState={resetFilterSortState}
            baseSchema={baseSchema}
            addOrUpdatePropertyFilter={addOrUpdatePropertyFilter}
            addOrUpdatePerspectiveFilter={addOrUpdatePerspectiveFilter}
            removePropertyFilterByKey={removePropertyFilterByKey}
            addOrUpdateSortOrder={addOrUpdateSortOrder}
            removeSortOrderByKey={removeSortOrderByKey}
            organizationId={nodesData[0] ? nodesData[0].properties.sphereId : 1}
          />
        </div>

        <AtlasHeader
          onlyNodeLabel={false}
          filterSortState={filterSortState}
          featuredSphereId={nodesData[0]?.id}
          featuredSphereDirection={nodeDirections[nodesData[0]?.id] || 'outwards'}
          featuredSphereHops={Math.max(getMaxExpansionLevel(), 1)} // Use expansion level but minimum 1 hop
          resetFilterSortState={resetFilterSortState}
          baseSchema={baseSchema}
          addOrUpdatePropertyFilter={addOrUpdatePropertyFilter}
          addOrUpdatePerspectiveFilter={addOrUpdatePerspectiveFilter}
          removePropertyFilterByKey={removePropertyFilterByKey}
          addOrUpdateSortOrder={addOrUpdateSortOrder}
          removeSortOrderByKey={removeSortOrderByKey}
          organizationId={nodesData[0] ? nodesData[0].properties.sphereId : 1}
          composedColumnFilters={composedColumnFilters}
          columnFilterSaveTargets={columnFilterSaveTargets}
          onColumnFilterSave={onColumnFilterSave}
          activeViewDisplayName={activeViewDisplayName}
          arcTemplates={inUseArcTemplates}
          allSchemas={allSchemas}
          onIndividualWidthsCalculated={setHeaderWidths} // Pass set function
          onHeaderWidthCalculated={(width) => {
            setHeaderWidth(width);
          }}
        />
        <ul
          className="flat-tree pl-0 mt-0"
          ref={treeRef}
          style={{
            minWidth: `${headerWidth - (firstHeaderRef.current?.offsetWidth ?? 0)}px`,
          }}
        >
          {_contextLoading && nodesData.length === 0
            ? Array.from({ length: 6 }).map((_, i) => (
                <li key={`skeleton-row-${i}`} className="p-treenode">
                  <div className="p-treenode-content atlas-skeleton-row">
                    <Skeleton borderRadius="26px" width="100%" height="30px" />
                  </div>
                </li>
              ))
            : _contextError
              ? (
                <li className="atlas-empty-state atlas-error-state">
                  <i className="pi pi-exclamation-triangle" />
                  <span>{t('atlas.error.load_failed', 'Failed to load data. Please try again.')}</span>
                </li>
              )
              : !_contextLoading && nodesData.length === 0
                ? (
                  <li className="atlas-empty-state">
                    <i className="pi pi-inbox" />
                    <span>{t('atlas.empty.no_items', 'No items found.')}</span>
                  </li>
                )
                : (
                  <>
                    {nodesData.map((node) => renderNode(node, 0, true))}
                    {isChildless && (
                      <li key="atlas-empty-state" className="atlas-empty-state-container">
                        <AtlasEmptyStateMolecule
                          sphere={nodesData[0]}
                          orgId={emptyStateOrgId}
                          availableTypes={availableTypesForEmpty}
                          typesLoading={availableTypesLoading}
                          suggestedTemplates={suggestedTemplatesForEmpty}
                          recentTypes={recentTypesForEmpty}
                          onQuickCaptureCapture={(newSphere) =>
                            handleSaveSphere(newSphere, nodesData[0].id)
                          }
                          onOpenDialog={(tab, schema) => {
                            if (schema) {
                              // "Start blank" type chip — open straight on Configure
                              // for the chosen type.
                              setNodeParentSphere(nodesData[0] ?? null);
                              setAddSphereV2InitialSchema(schema);
                              setAddSphereV2InitialTab('blank');
                              setAddSphereV2DialogVisible(true);
                            } else {
                              setAddSphereV2InitialSchema(null);
                              ShowTemplateDialog(tab);
                            }
                          }}
                          onDeploy={() => {
                            setNodeParentSphere(nodesData[0]);
                            setAddSphereV2InitialMode('deploy');
                            setAddSphereV2DialogVisible(true);
                          }}
                          onBrowseMarketplace={() => ShowTemplateDialog('template')}
                        />
                      </li>
                    )}
                  </>
                )}
        </ul>

        <DialogWrapper
          header="Add Sphere"
          visible={addSphereDialogVisible}
          onHide={handleHideAddSphereDialog}
          className="add-sphere-dialog"
          style={{ width: '90vw', maxWidth: '1800px' }}
        >
          <AddSphere
            parentSphere={nodeParentSphere ?? nodesData[0]}
            grandparentSphere={nodeGrandparentSphere || undefined}
            onSave={handleSaveSphere}
            onClose={handleHideAddSphereDialog}
            useCases={undefined}
            schema={selectedTemplateSchema}
          />
        </DialogWrapper>
        {/*
            Extract the nested ternary for header into a variable for clarity.
            */}
        {(() => {
          let templateDialogHeader;
          if (selectedSphereType) {
            if (selectedSphereType.source === 'type') {
              templateDialogHeader = `${t('addTemplate.TypeSelected', { defaultValue: 'Type Selected' })} (${selectedSphereType.label})`;
            } else {
              templateDialogHeader = `${t('addTemplate.useCaseSelected', { defaultValue: 'Use Case Selected' })} (${selectedSphereType.label})`;
            }
          } else {
            templateDialogHeader = t('addTemplate.addTemplateheader', { defaultValue: 'Add Template' });
          }
          return (
            <DialogWrapper
              header={templateDialogHeader}
              visible={templateDialogVisible}
              onHide={() => {
                setSelectedSphereType(null); // Reset selection when dialog closes
                HideTemplateDialog();
              }}
              className="add-template-dialog"
              style={{ width: '85vw', maxWidth: '1600px', height: '80vh' }}
            >
              <AddTemplate
                onSphereTypeSelect={handleSphereTypeSelect}
                assigneeSystemName={nodesData[0]?.properties?.type ?? ''}
                assigneeNodeLabel={nodesData[0]?.label}
                sphereId={nodesData[0]?.id}
                useCaseFilter={undefined}
                onUseTemplate={handleUseTemplate}
                showPreviewButton={true}
              />
            </DialogWrapper>
          );
        })()}

        <DialogWrapper header={editViewMode ? 'View Sphere' : 'Edit Sphere'} visible={editSphereDialogVisible} onHide={handleHideEditSphereDialog}>
          {nodeSphere && (
            <EditSphere
              sphere={nodeSphere}
              parentSphere={nodeParentSphere ?? nodesData[0]}
              onSave={handleEditSphere}
              onSphereRefresh={handleSphereRefresh}
              onClose={handleHideEditSphereDialog}
              viewMode={editViewMode}
              fetchPermissionList={fetchPermissionListHandler}
              initialTab={initialEditorTab}
            />
          )}
        </DialogWrapper>

        {/* Add Sphere dialog (2-step Choose → Configure) */}
        {nodesData[0] && (
          <AddSphereDialogOrganism
            visible={addSphereV2DialogVisible}
            onHide={() => {
              setAddSphereV2DialogVisible(false);
              setAddSphereV2InitialMode('create');
              setAddSphereV2InitialSchema(null);
              setDeployApproachTargetSphere(null);
            }}
            parentSphere={nodeParentSphere ?? nodesData[0]}
            initialTab={addSphereV2InitialTab}
            initialMode={addSphereV2InitialMode}
            initialSchema={addSphereV2InitialSchema}
            targetSphere={deployApproachTargetSphere ?? undefined}
            onOpenSphere={handleOpenDeployedSphere}
            onSphereCreated={(newSphere) => {
              setAddSphereV2DialogVisible(false);
              setAddSphereV2InitialMode('create');
              setAddSphereV2InitialSchema(null);
              setDeployApproachTargetSphere(null);
              handleSaveSphere(newSphere, (nodeParentSphere ?? nodesData[0]).id);
            }}
          />
        )}
      </div>
      {/* Inline draft quick-capture — pinned above the footer, outside the scrolling list */}
      {inlineAddParentId && nodesData[0] && inlineAddParentId === nodesData[0].id && (
        <div className="atlas-inline-add-dock">
          <InlineAddSphereRowMolecule
            parentSphere={nodesData[0]}
            onCapture={(newSphere) => {
              setInlineAddParentId(null);
              handleSaveSphere(newSphere, nodesData[0].id);
            }}
            onOpenDialog={(tab) => {
              setInlineAddParentId(null);
              ShowTemplateDialog(tab);
            }}
            onCancel={() => setInlineAddParentId(null)}
          />
        </div>
      )}
      {/* Footer add + pagination — hidden in the empty state, which carries its own
          add affordances and has no pages to page through. */}
      {!isChildless && (
      <div className="atlas-pagination">
        {!(viewConfig.hideAddSphere ?? false) && (
          <div className="col-1 sphere-buttons">
            <button
              className="add-sphere-button w-auto"
              onClick={() => setInlineAddParentId(nodesData[0]?.id ?? null)}
              disabled={!!inlineAddParentId}
            >
              {t('common.actions.add')}
            </button>
          </div>
        )}
        <div className="col-12 page-nav">
          <button
            className="page-prev"
            onClick={() => handlePrevPage(nodesData[0]?.id ?? '')}
            disabled={treeRef.current?.scrollTop === 0} // Disable only when at the top
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="icon-circle">
                <i className="pi pi-angle-left"></i>
              </span>
              {t('common.pagination.back')}
            </div>
          </button>
          <button className="page-next" onClick={() => handleNextPage(nodesData[0]?.id ?? '')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {t('common.pagination.next')}
              <span className="icon-circle">
                <i className="pi pi-angle-right"></i>
              </span>
            </div>
          </button>
        </div>
      </div>
      )}
    </StatusPopoverProvider>
    </AtlasViewContext.Provider>
  );
});

AtlasTree.displayName = 'AtlasTree';
export default AtlasTree;
